import { supabase } from '../supabase.js'

export const MARKET_DATA_LIVE_STAGING_FANOUT_HOST = 'market-data-live-staging.bigworldcapitalgroup.com'
export const MARKET_DATA_PRODUCTION_FANOUT_HOST = 'market-data.bigworldcapitalgroup.com'

const PRODUCTION_ORIGIN = 'https://erevis.bigworldcapitalgroup.com'
const LIVE_STAGING_ORIGIN = 'https://erevis-live-staging.bigworldcapitalgroup.com'
const LEGACY_PRODUCTION_GATEWAY_HOST = 'gateway.bigworldcapitalgroup.com'
const LIVE_STAGING_DEFAULT_FANOUT_URL = `https://${MARKET_DATA_LIVE_STAGING_FANOUT_HOST}/market-data/stream`
const RUNTIME_FANOUT_BAR_SIZES = new Set(['1min', '2min', '5min', '15min', '30min', '65min', '1day'])
const DEFAULT_RECONNECT_BASE_MS = 1000
const DEFAULT_RECONNECT_MAX_MS = 30000
const DEFAULT_SILENCE_TIMEOUT_MS = 45000
const DEFAULT_DATA_SILENCE_TIMEOUT_MS = 60000

function readRuntimeFanoutUrl() {
  return import.meta.env?.VITE_MARKET_DATA_FANOUT_URL || null
}

function currentOrigin() {
  if (typeof window === 'undefined') return ''
  return window.location?.origin || ''
}

function normalizeOrigin(value) {
  return String(value || '').trim().toLowerCase().replace(/\/$/, '')
}

export function resolveRuntimeFanoutBaseUrl({
  baseUrl = readRuntimeFanoutUrl(),
  origin = currentOrigin(),
} = {}) {
  const normalizedOrigin = normalizeOrigin(origin)
  const effectiveBaseUrl = baseUrl || (
    normalizedOrigin === LIVE_STAGING_ORIGIN ? LIVE_STAGING_DEFAULT_FANOUT_URL : null
  )
  if (!effectiveBaseUrl) return null

  const url = new URL(effectiveBaseUrl)
  const hostname = String(url.hostname || '').toLowerCase()

  if (hostname === LEGACY_PRODUCTION_GATEWAY_HOST) return null
  if (normalizedOrigin === PRODUCTION_ORIGIN && hostname === MARKET_DATA_LIVE_STAGING_FANOUT_HOST) return null

  return url.toString()
}

export function buildRuntimeFanoutUrl({
  symbol,
  barSize,
  session = 'regular',
  token = null,
  baseUrl = readRuntimeFanoutUrl(),
  origin = currentOrigin(),
} = {}) {
  const normalizedSymbol = normalizeSymbol(symbol)
  if (!normalizedSymbol) throw new Error('runtime fanout symbol is required')
  if (!RUNTIME_FANOUT_BAR_SIZES.has(barSize)) throw new Error(`unsupported runtime fanout barSize: ${String(barSize)}`)
  const resolvedBaseUrl = resolveRuntimeFanoutBaseUrl({ baseUrl, origin })
  if (!resolvedBaseUrl) throw new Error('runtime fanout URL is not configured for this runtime')
  const url = new URL(resolvedBaseUrl)
  url.searchParams.set('symbol', normalizedSymbol)
  url.searchParams.set('barSize', barSize)
  url.searchParams.set('session', session === 'all' ? 'all' : 'regular')
  if (token) url.searchParams.set('token', token)
  return url.toString()
}

export async function fetchRuntimeFanoutToken({
  symbol,
  barSize,
  session = 'regular',
  fetchImpl = globalThis.fetch,
  getAccessToken = getSupabaseAccessToken,
  origin = currentOrigin() || 'http://localhost',
} = {}) {
  if (typeof fetchImpl !== 'function') throw new Error('runtime fanout token fetch is unavailable')
  const accessToken = await getAccessToken()
  if (!accessToken) throw new Error('runtime fanout token requires authentication')
  const normalizedSymbol = normalizeSymbol(symbol)
  if (!normalizedSymbol) throw new Error('runtime fanout symbol is required')

  const url = new URL('/api/marketData/fanout-token', origin)
  url.searchParams.set('symbol', normalizedSymbol)
  url.searchParams.set('barSize', barSize)
  url.searchParams.set('session', session === 'all' ? 'all' : 'regular')

  const response = await fetchImpl(url.toString(), {
    headers: { Authorization: `Bearer ${accessToken}` },
    cache: 'no-store',
  })
  if (!response.ok) throw new Error(`runtime fanout token failed: ${response.status}`)
  const body = await response.json()
  if (!body?.token) throw new Error('runtime fanout token response missing token')
  return body.token
}

export function subscribeRuntimeBars({
  symbol,
  barSize,
  session = 'regular',
  onBar,
  onLastPrice,
  onError,
  onStatus,
  EventSourceImpl = globalThis.EventSource,
  tokenProvider = fetchRuntimeFanoutToken,
  baseUrl,
  origin = currentOrigin(),
  setTimeoutImpl = globalThis.setTimeout,
  clearTimeoutImpl = globalThis.clearTimeout,
  silenceTimeoutMs = DEFAULT_SILENCE_TIMEOUT_MS,
  dataSilenceTimeoutMs = DEFAULT_DATA_SILENCE_TIMEOUT_MS,
} = {}) {
  if (typeof onBar !== 'function') throw new Error('runtime fanout onBar callback is required')
  if (typeof EventSourceImpl !== 'function') return null

  const resolvedBaseUrl = resolveRuntimeFanoutBaseUrl({ baseUrl, origin })
  if (!resolvedBaseUrl) return null

  const state = {
    closed: false,
    source: null,
    reconnectTimer: null,
    silenceTimer: null,
    dataSilenceTimer: null,
    reconnectAttempt: 0,
  }

  const clearSilenceWatchdog = () => {
    if (state.silenceTimer && typeof clearTimeoutImpl === 'function') clearTimeoutImpl(state.silenceTimer)
    state.silenceTimer = null
  }

  const clearDataSilenceWatchdog = () => {
    if (state.dataSilenceTimer && typeof clearTimeoutImpl === 'function') clearTimeoutImpl(state.dataSilenceTimer)
    state.dataSilenceTimer = null
  }

  const closeSource = () => {
    if (!state.source) return
    try { state.source.close() } catch { /* noop */ }
    state.source = null
    clearSilenceWatchdog()
    clearDataSilenceWatchdog()
  }

  const scheduleReconnect = () => {
    if (state.closed || typeof setTimeoutImpl !== 'function') return
    if (state.reconnectTimer) return
    const delay = computeReconnectDelayMs(state.reconnectAttempt)
    state.reconnectAttempt += 1
    state.reconnectTimer = setTimeoutImpl(() => {
      state.reconnectTimer = null
      connect()
    }, delay)
  }

  const reconnectStalledSource = ({ source, reason, timeoutMs }) => {
    if (state.closed || state.source !== source) return
    notifyStatus(onStatus, 'disconnected', { reason, timeoutMs })
    if (typeof onError === 'function') onError(new Error(`runtime fanout ${reason} after ${timeoutMs}ms`))
    closeSource()
    scheduleReconnect()
  }

  const resetSilenceWatchdog = (source) => {
    const timeoutMs = positiveFiniteNumber(silenceTimeoutMs)
    if (!timeoutMs || typeof setTimeoutImpl !== 'function') return
    if (state.closed || state.source !== source) return
    clearSilenceWatchdog()
    state.silenceTimer = setTimeoutImpl(() => {
      reconnectStalledSource({ source, reason: 'silent-stream-timeout', timeoutMs })
    }, timeoutMs)
  }

  const resetDataSilenceWatchdog = (source) => {
    const timeoutMs = positiveFiniteNumber(dataSilenceTimeoutMs)
    if (!timeoutMs || typeof setTimeoutImpl !== 'function') return
    if (state.closed || state.source !== source) return
    clearDataSilenceWatchdog()
    state.dataSilenceTimer = setTimeoutImpl(() => {
      reconnectStalledSource({ source, reason: 'silent-data-timeout', timeoutMs })
    }, timeoutMs)
  }

  async function connect() {
    if (state.closed) return
    try {
      const token = typeof tokenProvider === 'function'
        ? await tokenProvider({ symbol, barSize, session })
        : null
      if (state.closed) return
      const url = buildRuntimeFanoutUrl({ symbol, barSize, session, token, baseUrl: resolvedBaseUrl, origin })
      const source = new EventSourceImpl(url)
      state.source = source
      notifyStatus(onStatus, 'connecting')
      resetSilenceWatchdog(source)

      source.addEventListener?.('open', () => {
        state.reconnectAttempt = 0
        resetSilenceWatchdog(source)
        notifyStatus(onStatus, 'live')
      })
      source.addEventListener?.('ready', () => {
        state.reconnectAttempt = 0
        resetSilenceWatchdog(source)
        notifyStatus(onStatus, 'live')
      })
      source.addEventListener?.('heartbeat', () => {
        resetSilenceWatchdog(source)
        notifyStatus(onStatus, 'live')
      })
      source.addEventListener?.('degraded', (event) => {
        resetSilenceWatchdog(source)
        notifyStatus(onStatus, 'degraded', parseEventData(event))
      })
      source.addEventListener?.('bar', (event) => {
        try {
          resetSilenceWatchdog(source)
          const payload = JSON.parse(event.data)
          if (payload?.type === 'bar') {
            resetDataSilenceWatchdog(source)
            onBar(withBrowserLatency(payload))
          }
        } catch (err) {
          if (typeof onError === 'function') onError(err)
        }
      })
      source.addEventListener?.('lastPrice', (event) => {
        try {
          resetSilenceWatchdog(source)
          const payload = JSON.parse(event.data)
          if (payload?.type === 'lastPrice' && typeof onLastPrice === 'function') {
            resetDataSilenceWatchdog(source)
            onLastPrice(withBrowserLatency(payload))
          }
        } catch (err) {
          if (typeof onError === 'function') onError(err)
        }
      })
      source.onerror = (event) => {
        notifyStatus(onStatus, 'disconnected')
        if (typeof onError === 'function') onError(event)
        closeSource()
        scheduleReconnect()
      }
    } catch (err) {
      notifyStatus(onStatus, 'disconnected')
      if (typeof onError === 'function') onError(err)
      scheduleReconnect()
    }
  }

  connect()

  return () => {
    state.closed = true
    if (state.reconnectTimer && typeof clearTimeoutImpl === 'function') clearTimeoutImpl(state.reconnectTimer)
    state.reconnectTimer = null
    closeSource()
  }
}

function normalizeSymbol(value) {
  return typeof value === 'string' && value.trim() ? value.trim().toUpperCase() : null
}

async function getSupabaseAccessToken() {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token || null
}

function notifyStatus(callback, state, detail = null) {
  if (typeof callback === 'function') callback({ state, detail })
}

function parseEventData(event) {
  try {
    return event?.data ? JSON.parse(event.data) : null
  } catch {
    return null
  }
}

function withBrowserLatency(payload, nowMs = Date.now()) {
  if (!payload || typeof payload !== 'object') return payload
  const latency = payload.latency && typeof payload.latency === 'object'
    ? payload.latency
    : {}
  const fanoutSendTs = integerOrNull(latency.fanoutSendTs)
  const browserReceiveTs = nowMs
  return {
    ...payload,
    latency: {
      ...latency,
      browserReceiveTs,
      fanoutToBrowserMs: diffMs(browserReceiveTs, fanoutSendTs),
      providerToBrowserMs: diffMs(browserReceiveTs, integerOrNull(latency.providerEventTs)),
    },
  }
}

function integerOrNull(value) {
  const number = Number(value)
  return Number.isInteger(number) ? number : null
}

function diffMs(later, earlier) {
  return Number.isInteger(later) && Number.isInteger(earlier) ? later - earlier : null
}

function positiveFiniteNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number : null
}

function computeReconnectDelayMs(attempt) {
  const safeAttempt = Math.max(0, Number.isFinite(Number(attempt)) ? Math.floor(Number(attempt)) : 0)
  return Math.min(DEFAULT_RECONNECT_BASE_MS * (2 ** safeAttempt), DEFAULT_RECONNECT_MAX_MS)
}
